import axios from 'axios';
import fallbackApi from './apiFallback';
import { getFriendlyErrorMessage } from '../lib/friendlyErrors';

// Get the appropriate base URL based on environment
const getBaseURL = () => {
  // Check if we're in development mode
  if (import.meta.env.DEV) {
    // In development, use direct backend URL to avoid proxy issues
    console.log(' Development mode - using direct backend URL');
    return 'http://localhost:5000/api';
  }
  
  // In production, use the environment variable or fallback to Render backend URL
  const productionURL = import.meta.env.VITE_API_BASE_URL;
  if (productionURL) {
    return productionURL + '/api';
  }
  
  // Fallback - Render backend URL (update this with your actual Render URL)
  return 'https://fleet-app-x1ly.onrender.com/api';
};

// Request deduplication map
const pendingRequests = new Map();

// Create axios instance
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 35000, // Increased timeout to match backend (30s + 5s buffer)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Create a unique key for this request (for cleanup purposes only)
    const requestKey = `${config.method?.toUpperCase()}_${config.url}`;

    // Add this request to pending requests with timestamp (for cleanup only)
    pendingRequests.set(requestKey, { timestamp: Date.now() });

    if (import.meta.env.DEV) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      console.log('Request Config:', {
        baseURL: config.baseURL,
        url: config.url,
        timeout: config.timeout,
        headers: config.headers
      });
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Store the request key for cleanup
    config.requestKey = requestKey;
    
    return config;
  },
  (error) => {
    console.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors, logging, and cleanup
api.interceptors.response.use(
  (response) => {
    // Clean up pending request
    if (response.config.requestKey) {
      pendingRequests.delete(response.config.requestKey);
    }

    if (import.meta.env.DEV) {
      console.log(
        `API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`
      );
    }
    return response;
  },
  (error) => {
    // Clean up pending request on error
    if (error.config?.requestKey) {
      pendingRequests.delete(error.config.requestKey);
    }

    if (import.meta.env.DEV) {
      console.error(`API Error: ${error.code || 'UNKNOWN'} - ${error.message}`);
      console.error('Error Details:', {
        config: error.config,
        response: error.response,
        request: error.request
      });
    }
    
    if (error.response?.status === 401) {
      const responseError = String(error.response?.data?.error || '').toLowerCase();
      const isHardAuthFailure =
        responseError.includes('invalid or expired token') ||
        responseError.includes('invalid token') ||
        responseError.includes('token expired');

      // Only force logout for real token failures. Avoid logging users out for
      // endpoint-specific 401s or transient backend conditions.
      if (isHardAuthFailure) {
        console.warn('Forcing logout due to hard auth failure on request:', {
          url: error.config?.url,
          method: error.config?.method,
          message: error.response?.data?.error
        });
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    error.friendlyMessage = getFriendlyErrorMessage(error);
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.error = error.friendlyMessage;
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  signup: (userData) => api.post('/auth/signup', userData),
  signin: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  refreshToken: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  // Signup with invitation token
  signupWithInvitation: (userData, invitationToken) => api.post('/auth/signup', { ...userData, invitationToken }),
};

// Users API calls
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  deactivate: (id) => api.delete(`/users/${id}`),
  reactivate: (id) => api.post(`/users/${id}/activate`),
  deletePermanent: (id) => api.delete(`/users/${id}/permanent`, { timeout: 120000 }),
  getStats: () => api.get('/users/stats/overview'),
};

// Robots API calls
export const robotsAPI = {
  getAll: (params) => api.get('/robots', { params }),
  getById: (id) => api.get(`/robots/${id}`),
  create: (robotData) => api.post('/robots', robotData),
  update: (id, robotData) => api.put(`/robots/${id}`, robotData),
  deactivate: (id) => api.delete(`/robots/${id}`),
  addAlert: (id, alertData) => api.post(`/robots/${id}/alerts`, alertData),
  resolveAlert: (id, alertId) => api.put(`/robots/${id}/alerts/${alertId}/resolve`),
  getStats: () => api.get('/robots/stats/overview'),
};

// Health check
export const healthCheck = () => api.get('/health');

// Organizations API calls
export const organizationsAPI = {
  // Public endpoint for signup dropdown
  getAll: () => api.get('/organizations'),
  
  // Admin endpoints
  getAllWithStats: () => api.get('/organizations/stats'),
  create: (data) => api.post('/organizations', data),
  update: (orgId, data) => api.put(`/organizations/${orgId}`, data),
  delete: (orgId) => api.delete(`/organizations/${orgId}`),
  
  // User endpoints
  getDetail: (orgId) => api.get(`/organizations/${orgId}/detail`),
  getUsers: (orgId, params) => api.get(`/organizations/${orgId}/users`, { params }),
  updateUser: (orgId, userId, data) => api.put(`/organizations/${orgId}/users/${userId}`, data),
  deactivateUser: (orgId, userId) => api.delete(`/organizations/${orgId}/users/${userId}`),
  deleteUserPermanent: (orgId, userId) =>
    api.delete(`/organizations/${orgId}/users/${userId}/permanent`, { timeout: 120000 }),
  activateUser: (orgId, userId) => api.post(`/organizations/${orgId}/users/${userId}/activate`),
  getRobots: (orgId) => api.get(`/organizations/${orgId}/robots`),
  getMissionMaps: (orgId) => api.get(`/organizations/${orgId}/mission-maps`)
};

// Invitations API calls
export const invitationsAPI = {
  create: (data) => api.post('/invitations', data),
  getAll: (params = {}) => api.get('/invitations', { params }),
  getByToken: (token) => api.get(`/invitations/${token}`),
  resend: (id) => api.post(`/invitations/${id}/resend`),
  cancel: (id) => api.delete(`/invitations/${id}`),
  deletePermanent: (id) => api.delete(`/invitations/${id}/permanent`),
};

// Contracts API calls
export const contractsAPI = {
  getAll: () => api.get('/contracts'),
  getById: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  createServiceRequest: (contractId, data) => api.post(`/contracts/${contractId}/service-requests`, data),
  updateServiceRequest: (requestId, data) => api.put(`/contracts/service-requests/${requestId}`, data),
  renew: (id, data) => api.post(`/contracts/${id}/renew`, data),
};

// Profile API calls
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  updatePassword: (data) => api.put('/profile/password', data),
  updateNotifications: (data) => api.put('/profile/notifications', data),
};

export const organizationRobotMappingsAPI = {
  getAll: (params) => api.get('/organization-robot-mappings', { params }),
  create: (data) => api.post('/organization-robot-mappings', data),
  unassign: (id) => api.put(`/organization-robot-mappings/${id}/unassign`),
  update: (id, data) => api.put(`/organization-robot-mappings/${id}`, data),
  getStats: () => api.get('/organization-robot-mappings/stats/overview')
};

export const siteMappingsAPI = {
  getOverview: () => api.get('/site-mappings/overview'),
  getOrgSites: (orgId) => api.get(`/site-mappings/organizations/${orgId}`),
  assignMapToOrg: (mapId, organizationId) =>
    api.put(`/site-mappings/mission-maps/${mapId}/organization`, { organizationId }),
  unassignMapFromOrg: (mapId) =>
    api.delete(`/site-mappings/mission-maps/${mapId}/organization`),
  assignRobotToSite: (mapId, robotId, notes = '') =>
    api.post(`/site-mappings/mission-maps/${mapId}/robots`, { robotId, notes }),
  unassignRobotFromSite: (mapId, robotId) =>
    api.delete(`/site-mappings/mission-maps/${mapId}/robots/${robotId}`),
  getFieldOperatorSites: (userId) => api.get(`/site-mappings/field-operators/${userId}/sites`),
  setFieldOperatorSites: (userId, missionMapIds) =>
    api.put(`/site-mappings/field-operators/${userId}/sites`, { missionMapIds }),
};

export const sitesAPI = {
  getOverview: () => api.get('/sites/overview'),
  getOrgSites: (orgId) => api.get(`/sites/organizations/${orgId}`),
  getFleetAssets: (orgId) => api.get(`/sites/organizations/${orgId}/fleet-assets`),
  createSite: (orgId, data) => api.post(`/sites/organizations/${orgId}`, data),
  updateSite: (siteId, data) => api.put(`/sites/${siteId}`, data),
  deleteSite: (siteId) => api.delete(`/sites/${siteId}`),
  addZone: (siteId, data) => api.post(`/sites/${siteId}/zones`, data),
  assignRobot: (siteId, robotId, notes = '') =>
    api.post(`/sites/${siteId}/robots`, { robotId, notes }),
  unassignRobot: (siteId, robotId) => api.delete(`/sites/${siteId}/robots/${robotId}`),
  assignHardware: (siteId, hardwareAssetId, notes = '') =>
    api.post(`/sites/${siteId}/hardware`, { hardwareAssetId, notes }),
  unassignHardware: (siteId, assignmentId) =>
    api.delete(`/sites/${siteId}/hardware/${assignmentId}`),
  assignMissionMap: (siteId, mapId) => api.put(`/sites/${siteId}/mission-maps/${mapId}`),
  unassignMissionMap: (siteId, mapId) => api.delete(`/sites/${siteId}/mission-maps/${mapId}`),
  assignMissionMapToRobot: (siteId, robotId, mapId, notes = '') =>
    api.put(`/sites/${siteId}/robots/${robotId}/mission-maps/${mapId}`, { notes }),
  unassignMissionMapFromRobot: (siteId, robotId) =>
    api.delete(`/sites/${siteId}/robots/${robotId}/mission-map`),
  assignHardwareToRobot: (siteId, robotId, hardwareAssetId, notes = '') =>
    api.post(`/sites/${siteId}/robots/${robotId}/hardware`, { hardwareAssetId, notes }),
  unassignHardwareFromRobot: (siteId, robotId, assignmentId) =>
    api.delete(`/sites/${siteId}/robots/${robotId}/hardware/${assignmentId}`),
  assignTaskTypeToRobot: (siteId, robotId, taskType) =>
    api.put(`/sites/${siteId}/robots/${robotId}/task-type`, { taskType }),
  setUserSiteAssignments: (userId, siteIds) =>
    api.put(`/sites/users/${userId}/assignments`, { siteIds }),
  getUserSiteAssignments: (userId) => api.get(`/sites/users/${userId}/assignments`),
  orgSetup: (payload) => api.post('/sites/org-setup', payload),
};

export const hardwareAssetsAPI = {
  getCatalog: () => api.get('/hardware-assets/catalog'),
  getAll: () => api.get('/hardware-assets'),
  create: (data) => api.post('/hardware-assets', data),
  delete: (id) => api.delete(`/hardware-assets/${id}`),
  getAssignmentsOverview: () => api.get('/hardware-assets/assignments/overview'),
  assignToRobot: (robotId, hardwareAssetId, notes = '') =>
    api.post(`/hardware-assets/robots/${robotId}/assign`, { hardwareAssetId, notes }),
  unassignFromRobot: (robotId, assignmentId) =>
    api.delete(`/hardware-assets/robots/${robotId}/assignments/${assignmentId}`),
  getDeploymentByOrg: (organizationId) =>
    api.get('/hardware-assets/deployment-by-org', {
      params: organizationId ? { organizationId } : undefined,
    }),
};

// Smart API wrapper that tries proxy first, then fallback
export const smartApi = {
  get: async (url, config = {}) => {
    try {
      console.log(' Trying proxy API first...');
      return await api.get(url, config);
    } catch (error) {
      console.log(' Proxy failed, trying fallback API...');
      try {
        return await fallbackApi.get(url, config);
      } catch (fallbackError) {
        console.error(' Both proxy and fallback failed');
        throw fallbackError;
      }
    }
  },
  
  post: async (url, data, config = {}) => {
    try {
      console.log(' Trying proxy API first...');
      return await api.post(url, data, config);
    } catch (error) {
      console.log(' Proxy failed, trying fallback API...');
      try {
        return await fallbackApi.post(url, data, config);
      } catch (fallbackError) {
        console.error(' Both proxy and fallback failed');
        throw fallbackError;
      }
    }
  }
};

export default api;
